import { useState, useEffect, useMemo } from "react";
import api from "../api";

/**
 * Camada de conteúdo editável.
 *
 * Cada página pública lê seus textos de um single type do Strapi. Enquanto o
 * cliente não preencheu um campo (ou enquanto a requisição não voltou), o valor
 * padrão definido no próprio componente é usado -- o site nunca renderiza
 * vazio, e o build estático (SSR) sai com o texto padrão dentro do HTML.
 */

const CACHE_PREFIX = "bivver-content:";

// Cabeçalho, rodapé, FAB e página podem pedir o mesmo single type no mesmo
// render. Estes dois mapas garantem uma requisição por rota, não uma por
// componente.
const memoryCache = {};
const inflight = {};

function readCache(path) {
  if (memoryCache[path]) return memoryCache[path];
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem(CACHE_PREFIX + path);
    const parsed = raw ? JSON.parse(raw) : undefined;
    if (parsed) memoryCache[path] = parsed;
    return parsed;
  } catch (error) {
    return undefined;
  }
}

function writeCache(path, data) {
  memoryCache[path] = data;
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CACHE_PREFIX + path, JSON.stringify(data));
  } catch (error) {
    // localStorage cheio ou indisponível -- o cache é só otimização.
  }
}

/** Descarta o cache de uma rota (usado após salvar na área de edição). */
export function invalidate(path) {
  delete memoryCache[path];
  delete inflight[path];
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CACHE_PREFIX + path);
  } catch (error) {
    // idem
  }
}

function fetchOnce(path) {
  if (!inflight[path]) {
    inflight[path] = api
      .get(path)
      .then((response) => {
        writeCache(path, response.data);
        return response.data;
      })
      .finally(() => {
        delete inflight[path];
      });
  }
  return inflight[path];
}

/**
 * Carrega um single type para a tela de edição, já com os padrões por baixo.
 * Single type ainda sem entrada responde 404 no Strapi -- isso não é erro, só
 * significa "nunca foi salvo", e os campos abrem com o texto que está no ar.
 */
export async function loadForEditing(path, defaults) {
  try {
    const { data } = await api.get(path);
    return merge(defaults, data);
  } catch (error) {
    if (error.response && error.response.status === 404) return defaults;
    throw error;
  }
}

/**
 * Salva um single type. `files` mapeia campo -> fileList do antd Upload; só
 * arquivos novos (com originFileObj) são enviados.
 */
export async function saveSingleType({ path, values, token, files = {} }) {
  const data = new FormData();

  Object.keys(files).forEach((field) => {
    const item = files[field] && files[field][0];
    if (item && item.originFileObj) {
      data.append(`files.${field}`, item.originFileObj, item.name);
    }
  });
  data.append("data", JSON.stringify(values));

  const response = await api({
    method: "PUT",
    url: path,
    headers: { Authorization: `Bearer ${token}` },
    data,
  });
  invalidate(path);
  return response;
}

/** Texto da API só ganha do padrão quando não está vazio. */
export function pick(value, fallback) {
  if (typeof value === "string") {
    return value.trim() ? value : fallback;
  }
  return value === undefined || value === null ? fallback : value;
}

/** Lista da API só ganha do padrão quando tem pelo menos um item. */
export function pickList(list, fallback) {
  return Array.isArray(list) && list.length > 0 ? list : fallback;
}

/** Quebra um campo de texto multilinha em itens (uma linha = um item). */
export function toLines(value) {
  return (value || "")
    .split("\n")
    .map((line) => line.replace(/^\s*[-*•]\s*/, "").trim())
    .filter(Boolean);
}

/** URL absoluta de um arquivo do Strapi (ou undefined). */
export function mediaUrl(apiUrl, media) {
  return media?.url ? `${apiUrl}${media.url}` : undefined;
}

/** Junta os campos preenchidos da API por cima dos padrões. */
export function merge(defaults, data) {
  if (!data) return defaults;
  const merged = { ...defaults };
  Object.keys(data).forEach((key) => {
    const value = data[key];
    merged[key] = Array.isArray(value)
      ? pickList(value, defaults[key])
      : pick(value, defaults[key]);
  });
  return merged;
}

/**
 * Busca um single type e devolve `{ ...defaults, ...camposPreenchidos }`.
 * `defaults` deve ser uma referência estável (constante de módulo).
 */
export function useSingleType(path, defaults) {
  const [data, setData] = useState(() => readCache(path));

  useEffect(() => {
    let alive = true;
    fetchOnce(path)
      .then((fresh) => {
        if (alive) setData(fresh);
      })
      .catch(() => {
        // Sem API no ar o site continua servindo os textos padrão.
      });
    return () => {
      alive = false;
    };
  }, [path]);

  return useMemo(() => merge(defaults, data), [data, defaults]);
}
